import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Heart } from 'lucide-react';
import { Button } from './ui/button';

const Hero = ({ data, onDonateClick }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const percentage = Math.round((data.current / data.goal) * 100);

  // Line by line reveal animation
  const titleLines = [
    "Wakaf Pendidikan",
    "Untuk Masa Depan",
    "Generasi Ummah"
  ];

  return (
    <section ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950">
      {/* Animated background image with parallax */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/kdu-building-front.png)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/95 via-purple-900/92 to-indigo-950/95" />
      </motion.div>

      {/* Grain texture overlay */}
      <div className="absolute inset-0 z-10 opacity-[0.015]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")'
      }} />

      <motion.div 
        style={{ opacity }}
        className="relative z-20 container mx-auto px-6 py-32 text-center"
      >
        {/* Main title with line-by-line reveal */}
        <div className="mb-8 overflow-hidden">
          {titleLines.map((line, index) => (
            <motion.h1
              key={index}
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.2 + (index * 0.15),
                ease: [0.33, 1, 0.68, 1]
              }}
              className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-2"
            >
              {line}
            </motion.h1>
          ))}
        </div>

        {/* Subtitle */}
        <motion.p
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.33, 1, 0.68, 1] }}
          className="text-xl md:text-2xl text-purple-100 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          Bantu kami membina <span className="text-purple-300 font-semibold">Kompleks Darul Ummah (KDU)</span> - kompleks pendidikan Islam bersepadu untuk SRII DUAT di atas tanah wakaf 5 ekar di Sijangkang, Selangor.
        </motion.p>

        {/* Progress infographic */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7, delay: 1, ease: [0.33, 1, 0.68, 1] }}
          className="max-w-4xl mx-auto mb-12"
          data-testid="progress-infographic"
        >
          <div className="rounded-3xl overflow-hidden shadow-2xl bg-white">
            <img
              src="/images/kutipan-infographic.jpg"
              alt="Kutipan Terkini KDU setakat 30 September 2026"
              className="w-full h-auto block"
            />
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.3, ease: [0.33, 1, 0.68, 1] }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Button
            onClick={onDonateClick}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-6 text-lg rounded-full shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105 group"
          >
            <Heart className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Infaq Sekarang
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 2, ease: [0.33, 1, 0.68, 1] }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;